import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { collection, orderBy, query, where } from "firebase/firestore";
import { customFirestore } from "@/firebase";
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { AuthContext } from "@/auth/AuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { DateTime } from "luxon";
import { type Exception } from "@/utils/types";
import { getDocs } from "@firebase/firestore";
import { CircularProgress, TableCell } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import styled from "@emotion/styled";
import {
  DataGridPro,
  gridClasses,
  type GridColDef,
} from "@mui/x-data-grid-pro";
import CustomNoRowsOverlay from "@/components/custom-now-rows-overlay";
import { PieChart } from "@mui/x-charts-pro/PieChart";
import { ChartsWrapper } from "./styled-components";
import ExceptionsSparkline from "@/routes/sessions/exceptions-sparkline";

const ExceptionsPage = () => {
  useDocumentTitle("Exceptions");
  const { user } = useContext<any>(AuthContext);
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const classParam = urlSearchParams.get("class");
  const methodParam = urlSearchParams.get("method");
  const lineParam = urlSearchParams.get("line");
  const filenameParam = urlSearchParams.get("filename");
  const dateFromParam = urlSearchParams.get("from");

  const [date, setDate] = useState<Date>(() => {
    let parsedDateInt: number | undefined = parseInt(dateFromParam ?? "");
    parsedDateInt =
      parsedDateInt && !isNaN(parsedDateInt) ? parsedDateInt : undefined;
    if (parsedDateInt) {
      const parsedDate = DateTime.fromMillis(parsedDateInt).toJSDate();
      console.log(parsedDate);
      // Check if the date is valid
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    // Default to one week ago
    return DateTime.now().minus({ day: 1 }).startOf("day").toJSDate();
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "exceptions",
      date.getTime(),
      classParam,
      methodParam,
      lineParam,
      filenameParam,
    ],
    queryFn: async () => {
      try {
        const parsedLineInt = parseInt(lineParam ?? "");
        const parsedLine =
          lineParam && !isNaN(parsedLineInt) ? parsedLineInt : undefined;
        const firebaseQuery = query(
          collection(customFirestore, `exceptions`),
          where("organisationId", "==", user.companyId),
          where("timestamp", ">=", date.getTime()),
          // ...(filenameParam ? [where("class", "==", filenameParam)] : []),
          ...(classParam ? [where("class", "==", classParam)] : []),
          ...(methodParam ? [where("method", "==", methodParam)] : []),
          ...(parsedLine ? [where("line", "==", parsedLine)] : []),
          orderBy("timestamp", "desc")
        );

        const response = await getDocs(firebaseQuery);

        return response.docs.map((doc) => {
          return {
            id: doc.id,
            ...(doc.data() as Omit<Exception, "id">),
            timestamp: DateTime.fromMillis(doc.data().timestamp).toJSDate(),
          } satisfies Exception;
        });
      } catch (e) {
        console.log(e);
        return [];
      }
    },
  });

  return (
    <div className="flex-1 flex flex-col bg-[#D9D9D9] h-full">
      <div className="flex flex-row flex-1 h-[calc(100vh_-_60px)]">
        {isLoading && (
          <div
            className={
              "flex flex-col h-full items-center justify-center w-full"
            }
          >
            <CircularProgress />
          </div>
        )}
        {error && <p>Error: </p>}
        {!isLoading && !error && data != null && (
          <ReportSuccess date={date} setDate={setDate} exceptions={data} />
        )}
      </div>
    </div>
  );
};

function ReportSuccess({
  date,
  exceptions,
  setDate,
}: {
  date: Date;
  setDate: Dispatch<SetStateAction<Date>>;
  exceptions: Exception[];
}) {
  const chartValues = useMemo(() => {
    const sums = exceptions.reduce(
      (prevException, currentException) => {
        const healed = currentException.healedLocator !== "None";

        return {
          healed: prevException.healed + (healed ? 1 : 0),
          nonHealed: prevException.nonHealed + (!healed ? 1 : 0),
        };
      },
      { healed: 0, nonHealed: 0 }
    );

    return Object.keys(sums).map((key) => ({
      id: key,
      label: key === "healed" ? "Healed" : "Non-healed",
      value: sums[key as keyof typeof sums],
      color: key === "healed" ? "rgb(34, 191, 117)" : "rgb(250, 79, 88)",
    }));
  }, [exceptions]);

  const sparklineData = useMemo(() => {
    const weeklyExceptions =
      exceptions
        ?.toSorted((a, b) => {
          return a.timestamp - b.timestamp;
        })
        ?.reduce<Record<string, Exception[]>>((acc, curr) => {
          console.log("curr", curr);
          const weekStart = DateTime.fromJSDate(curr.timestamp).startOf("week");
          const weekEnd = DateTime.fromJSDate(curr.timestamp).endOf("week");
          const weekFormat = "yyyy-MM-dd";
          const weekNumber = `${weekStart.toFormat(
            weekFormat
          )} - ${weekEnd.toFormat(weekFormat)}`;

          acc[weekNumber] = [...(acc[weekNumber] ?? []), curr];
          return acc;
        }, {}) ?? [];

    return Object.entries(weeklyExceptions).map(([week, exceptions]) => ({
      exceptions: exceptions.filter((e) => e.healedLocator !== "None"),
      week,
    }));
  }, [exceptions]);

  return (
    <ReportsSuccessPageWrapper>
      <ReportsSuccessPageHeader>
        <p className={"text-[24px] font-medium"}>Exceptions</p>
        {exceptions.length > 0 && (
          <ChartsWrapper>
            <PieChart
              width={172}
              height={70} // Reduced from 120 to 70
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              series={[
                {
                  data: chartValues,
                  innerRadius: 20, // Reduced from 30
                  outerRadius: 60, // Reduced from 100
                  paddingAngle: 3,
                  cornerRadius: 5,
                  startAngle: -90,
                  endAngle: 90,
                  cx: 100, // Center horizontally
                  cy: 65, // Position near bottom of the 70px height
                },
              ]}
            />
            {sparklineData.length > 1 && (
              <ExceptionsSparkline data={sparklineData} />
            )}
          </ChartsWrapper>
        )}
        <DatePicker
          label={"From Date"}
          value={DateTime.fromJSDate(date)}
          onChange={(value) => {
            setDate(value!.toJSDate());
          }}
        />
      </ReportsSuccessPageHeader>
      <Exceptions exceptions={exceptions} />
    </ReportsSuccessPageWrapper>
  );
}

const ReportsSuccessPageHeader = styled.div`
  grid-area: header;
  display: flex;
  justify-content: space-between;
  padding-right: 4px;
`;

const ReportsSuccessPageWrapper = styled.div`
  display: grid;
  grid-template-areas:
    "header"
    "sessions";
  width: 100%;
  grid-template-rows: auto minmax(0, 1fr);
  padding: 16px;
  margin: 16px;
  background-color: #e8e8e8;
  border-radius: 8px;
  gap: 24px;
`;

const Exceptions = ({ exceptions }: { exceptions: Exception[] }) => {
  const navigate = useNavigate();

  const navigateTo = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const columns: GridColDef<Exception>[] = [
    {
      field: "filename",
      headerName: "File Name",
      filterable: false,
      sortable: true,
    },
    {
      field: "line",
      headerName: "Line",
      filterable: true,
      sortable: true,
    },
    {
      field: "class",
      headerName: "Class",
      filterable: false,
      sortable: true,
    },
    {
      field: "method",
      headerName: "Method",
      filterable: true,
      sortable: true,
    },
    {
      field: "originalLocator",
      headerName: "Original Locator",
      filterable: true,
      sortable: true,
    },
    {
      field: "healedLocator",
      headerName: "Healed Locator",
      filterable: true,
      sortable: true,
    },
    {
      field: "timestamp",
      headerName: "Time",
      flex: 1,
      type: "dateTime",
      filterable: true,
      sortable: true,
      renderCell: (params) => {
        return (
          <TableCell>
            {DateTime.fromJSDate(params.value).toFormat("yyyy-MM-dd hh:mm a")}
          </TableCell>
        );
      },
    },
  ];

  return (
    <DataGridPro
      autosizeOptions={{
        includeHeaders: true, // Make sure headers are included in width calculation
        includeOutliers: true, // Include outlier values in calculation
        expand: true, // Expand columns to use available space
      }}
      autosizeOnMount={true}
      rows={exceptions ?? []}
      onRowClick={(params) => {
        navigateTo(
          `/gpt-driver/sessions/${params.row.sessionId}?exception=${params.row.id}`
        );
      }}
      initialState={{
        sorting: { sortModel: [{ field: "timestamp", sort: "desc" }] },
      }}
      showCellVerticalBorder={false}
      showColumnVerticalBorder={false}
      disableColumnMenu={true}
      className={"bg-white"}
      columns={columns}
      slots={{ noRowsOverlay: CustomNoRowsOverlay }}
      slotProps={{
        noRowsOverlay: { title: "No exceptions yet" },
      }}
      isRowSelectable={(row) => false}
      sx={{
        [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
          outline: "transparent",
        },
        [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
          {
            outline: "none",
          },
        [`& .${gridClasses.row}:hover`]: {
          cursor: "pointer",
        },
      }}
    />
  );
};

export default ExceptionsPage;
